<template>
  <div class="border-l-2 border-gray-200 pl-4 ml-2">
    <div class="flex items-center gap-2 py-1 group">
      <!-- Expand/Collapse Button -->
      <button
        v-if="isContainer"
        @click="toggleExpand"
        class="w-5 h-5 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded"
      >
        <svg class="w-3 h-3 transition-transform" :class="{ 'rotate-90': isExpanded }" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd" />
        </svg>
      </button>
      <div v-else class="w-5"></div>

      <!-- Key -->
      <span v-if="!isEditing" class="font-medium text-gray-700">{{ nodeKey }}:</span>
      <input
        v-else
        v-model="editKey"
        class="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-dark"
        @keyup.enter="saveEdit"
        @keyup.esc="cancelEdit"
      />

      <!-- Value -->
      <div v-if="!isEditing" class="flex items-center gap-2 flex-1">
        <span v-if="!isContainer" :class="typeColor" @dblclick="startEdit" class="cursor-text">
          {{ valueType === 'string' ? '"' + nodeValue + '"' : nodeValue }}
        </span>
        <span v-else :class="typeColor">
          {{ valueType === 'array' ? '[' + nodeValue.length + ']' : '{' + Object.keys(nodeValue).length + '}' }}
        </span>
        <span class="text-xs text-gray-400 italic">{{ valueType }}</span>
      </div>
      <div v-else class="flex items-center gap-2 flex-1">
        <select
          v-model="selectedType"
          class="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-dark"
        >
          <option value="string">String</option>
          <option value="number">Number</option>
          <option value="boolean">Boolean</option>
          <option value="null">Null</option>
          <option value="object">Object</option>
          <option value="array">Array</option>
        </select>
        <input
          v-if="selectedType !== 'object' && selectedType !== 'array'"
          v-model="editValue"
          class="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-dark flex-1"
          @keyup.enter="saveEdit"
          @keyup.esc="cancelEdit"
        />
      </div>

      <!-- Actions -->
      <div class="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
        <button
          v-if="!isEditing"
          @click="startEdit"
          class="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded"
          title="Edit"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
        <button
          v-if="isContainer"
          @click="addChild"
          class="p-1 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded"
          title="Add child"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
          </svg>
        </button>
        <button
          @click="deleteNode"
          class="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded"
          title="Delete"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
          </svg>
        </button>
        <div v-if="isEditing" class="flex gap-1">
          <button
            @click="saveEdit"
            class="p-1 text-green-600 hover:bg-green-50 rounded"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
            </svg>
          </button>
          <button
            @click="cancelEdit"
            class="p-1 text-red-600 hover:bg-red-50 rounded"
          >
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Children -->
    <div v-if="isContainer && isExpanded" class="ml-2">
      <JsonNode
        v-for="(value, key) in nodeValue"
        :key="key"
        :node-key="key"
        :node-value="value"
        :path="[...path, key]"
        @update="(path, value, newKey) => $emit('update', path, value, newKey)"
        @delete="(path) => $emit('delete', path)"
        @add="(path, value) => $emit('add', path, value)"
      />
    </div>
  </div>
</template>

<script>
export default {
  name: 'JsonNode',
  props: {
    nodeKey: {
      type: [String, Number],
      required: true
    },
    nodeValue: {
      required: true
    },
    path: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      isExpanded: true,
      isEditing: false,
      editKey: this.nodeKey,
      editValue: '',
      selectedType: 'string'
    };
  },
  computed: {
    valueType() {
      if (this.nodeValue === null) return 'null';
      if (Array.isArray(this.nodeValue)) return 'array';
      return typeof this.nodeValue;
    },
    isContainer() {
      return this.valueType === 'object' || this.valueType === 'array';
    },
    typeColor() {
      const colors = {
        string: 'text-green-700',
        number: 'text-blue-700',
        boolean: 'text-purple-700',
        null: 'text-gray-500',
        object: 'text-orange-700',
        array: 'text-pink-700'
      };
      return colors[this.valueType] || 'text-gray-700';
    }
  },
  methods: {
    toggleExpand() {
      this.isExpanded = !this.isExpanded;
    },
    startEdit() {
      if (!this.isContainer) {
        this.isEditing = true;
        this.editValue = this.valueType === 'null' ? 'null' : String(this.nodeValue);
        this.selectedType = this.valueType === 'null' ? 'null' : this.valueType;
      }
    },
    cancelEdit() {
      this.isEditing = false;
      this.editKey = this.nodeKey;
    },
    saveEdit() {
      let newValue = this.editValue;

      switch (this.selectedType) {
        case 'number':
          newValue = Number(newValue);
          if (isNaN(newValue)) {
            alert('Invalid number');
            return;
          }
          break;
        case 'boolean':
          newValue = newValue.toLowerCase() === 'true';
          break;
        case 'null':
          newValue = null;
          break;
        case 'object':
          newValue = {};
          break;
        case 'array':
          newValue = [];
          break;
      }

      this.$emit('update', this.path, newValue, this.editKey);
      this.isEditing = false;
    },
    deleteNode() {
      if (confirm('Are you sure you want to delete this property?')) {
        this.$emit('delete', this.path);
      }
    },
    addChild() {
      const key = this.valueType === 'array' ? this.nodeValue.length : prompt('Enter property name:');
      if (key !== null && key !== '') {
        this.$emit('add', [...this.path, key], '');
      }
    }
  }
};
</script>

<style scoped>
/* JsonNode specific styles if needed */
</style>
